"use client";

import React from "react";
import { Button } from "../common/button";
import { TherapistSchedule } from "./therapist-schedule";

// Credential full names mapping
const CREDENTIAL_FULL_NAMES: Record<string, string> = {
  'RP': 'Registered Psychotherapist',
  'RP (Qualifying)': 'Registered Psychotherapist (Qualifying)',
  'RSW': 'Registered Social Worker',
  'MSW': 'Master of Social Work',
  'MSW, RSW': 'Master of Social Work, Registered Social Worker',
  'RN': 'Registered Nurse',
  'RPN': 'Registered Psychiatric Nurse',
  'OT': 'Occupational Therapist',
  'OT Reg. (Ont.)': 'Occupational Therapist Registered (Ontario)',
  'R. Psych': 'Registered Psychologist',
  'C. Psych': 'Clinical Psychologist',
  'RCSW': 'Registered Clinical Social Worker',
  'RCC': 'Registered Clinical Counsellor',
  'CCC': 'Canadian Certified Counsellor',
  'MACP': 'Master of Arts in Counselling Psychology',
  'LMFT': 'Licensed Marriage and Family Therapist',
  'LCSW': 'Licensed Clinical Social Worker',
  'RMFT': 'Registered Marriage and Family Therapist',
  'RP-Q': 'Registered Psychotherapist (Qualifying)',
  'RSW (Ont.)': 'Registered Social Worker (Ontario)',
  'RSW (BC)': 'Registered Social Worker (British Columbia)',
  'MA': 'Master of Arts',
  'M.Ed': 'Master of Education',
  'MEd': 'Master of Education',
  'PhD': 'Doctor of Philosophy',
  'PsyD': 'Doctor of Psychology'
};

export interface Supervisor {
  name: string;
  credential: string;
  credentials?: string[];
  yearsExperience: number;
  profileImage: string;
}

export interface TherapistPracticeInfoLabels {
  heading?: string;
  availabilityHeading?: string;
  languagesHeading?: string;
  /** Interpolated with `{noun}` (residentNoun) and `{name}`. Example:
   *  "As a {noun} under supervision, {name} provides services in
   *  English". */
  residentLanguagesNote?: string;
  /** Interpolated with `{name}` and `{languages}` (already-joined). */
  associateLanguagesSingular?: string;
  associateLanguagesPlural?: string;
  insuranceHeading?: string;
  insuranceIntro?: string;
  insuranceResidentNote?: string;
  /** Interpolated with `{supervisor}`. */
  underSupervisionOf?: string;
  clinicalSupervisionHeading?: string;
  /** Interpolated with `{name}`, `{supervisor}`, `{supervisorFirstName}`. */
  clinicalSupervisionBody?: string;
  residencyHeading?: string;
  residencyCompletesOn?: string;
  residencyCompleted?: string;
  residencyToday?: string;
  residencyOneDayLeft?: string;
  /** Interpolated with `{count}`. */
  residencyDays?: string;
  residencyOneMonth?: string;
  /** Interpolated with `{count}`. */
  residencyMonths?: string;
  residencyOneYear?: string;
  /** Interpolated with `{count}`. */
  residencyYears?: string;
  transitionBenefitTitle?: string;
  /** Interpolated with `{count}` (session count). */
  transitionBenefitBody?: string;
}

export interface TherapistPracticeInfoProps {
  schedule: any;
  timezone: string;
  therapistTier: "resident" | "associate";
  credential: string;
  credentialsWithFullNames?: { type: string; fullName: string }[];
  supervisor?: Supervisor;
  residencyCompletion?: string;
  firstName: string;
  languages?: string[];
  onExploreAvailability?: () => void;
  /** Singular noun used in inline copy referring to the tier — e.g.
   *  "As an {noun} under supervision, …". Default is "intern therapist"
   *  to match the rest of alli.io; imwell overrides to "resident
   *  therapist" to match its student-facing terminology. */
  residentNoun?: string;
  /** Optional label overrides for user-visible strings. */
  labels?: TherapistPracticeInfoLabels;
}

export function TherapistPracticeInfo({
  schedule,
  timezone,
  therapistTier,
  credential,
  credentialsWithFullNames = [],
  supervisor,
  residencyCompletion,
  firstName,
  languages = [],
  onExploreAvailability,
  residentNoun = "intern therapist",
  labels
}: TherapistPracticeInfoProps) {
  const fill = (template: string, vars: Record<string, string | number>): string =>
    Object.entries(vars).reduce(
      (acc, [k, v]) => acc.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v)),
      template,
    );
  return (
    <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 lg:p-8">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
        {labels?.heading ?? 'Practice Information'}
      </h2>

      <div className="space-y-6">
        {/* Schedule with Explore Button */}
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
            <h3 className="text-lg font-semibold text-gray-900">{labels?.availabilityHeading ?? 'Availability'}</h3>
            {/* Temporarily commenting out Check Real-Time Availability button
            <Button 
              variant="secondary" 
              size="sm"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2"
              onClick={onExploreAvailability}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Check Real-Time Availability
            </Button>
            */}
          </div>
          <TherapistSchedule 
            schedule={schedule} 
            timezone={timezone}
            showNationwide={
              !credentialsWithFullNames || 
              credentialsWithFullNames.length === 0 ||
              credentialsWithFullNames.some(cred => 
                cred.type === 'RP' || cred.type === 'RP (Qualifying)'
              )
            }
          />
        </div>
        
        {/* Languages */}
        {languages && languages.length > 0 && (
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 text-primary-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
              {labels?.languagesHeading ?? 'Languages Spoken'}
            </h3>
            <div className="flex flex-wrap gap-2">
              {languages.map((language, index) => (
                <div
                  key={index}
                  className="px-4 py-2 bg-white rounded-lg border border-gray-200 flex items-center gap-2"
                >
                  <svg className="w-4 h-4 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-gray-900">{language}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-3">
              {therapistTier === 'resident'
                ? fill(
                    labels?.residentLanguagesNote ??
                      `As a${/^[aeiou]/i.test(residentNoun) ? 'n' : ''} {noun} under supervision, {name} provides services in English`,
                    { noun: residentNoun, name: firstName },
                  )
                : fill(
                    languages.length === 1
                      ? labels?.associateLanguagesSingular ??
                          '{name} can provide therapy services in this language'
                      : labels?.associateLanguagesPlural ??
                          '{name} can provide therapy services in these languages',
                    { name: firstName, languages: languages.join(', ') },
                  )}
            </p>
          </div>
        )}
        
        {/* Insurance Coverage */}
        <div className="bg-gray-50 rounded-xl p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
            <svg className="w-5 h-5 text-primary-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {labels?.insuranceHeading ?? 'Insurance Coverage'}
          </h3>
          <p className="text-sm text-gray-700 mb-3">
            {labels?.insuranceIntro ?? 'Coverage depends on the insurance plan. Check with your provider to see if your plan covers one of the designations listed below:'}
          </p>
          {therapistTier === "resident" && (
            <p className="text-sm text-gray-600 italic mb-3">
              {labels?.insuranceResidentNote ?? '(If you are receiving services from a resident therapist, their work is covered under the designation of their supervisor.)'}
            </p>
          )}
          <div className="mt-3 space-y-2">
            {credentialsWithFullNames
              .filter(cred => 
                cred.type === 'RP' || 
                cred.type === 'RP (Qualifying)' || 
                cred.type === 'RSW'
              )
              .map((cred, index) => (
              <div key={index} className="p-3 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="font-medium text-gray-900">{cred.fullName}</p>
                    {therapistTier === "resident" && supervisor && (
                      <p className="text-xs text-gray-500">
                        {fill(
                          labels?.underSupervisionOf ?? 'Under supervision of {supervisor}',
                          { supervisor: supervisor.name },
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        
        {/* Supervisor Info - Only for residents */}
        {therapistTier === "resident" && supervisor && (
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 text-primary-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              {labels?.clinicalSupervisionHeading ?? 'Clinical Supervision'}
            </h3>
            <div className="flex items-start gap-4">
              {supervisor.profileImage ? (
                <img 
                  src={supervisor.profileImage}
                  alt={supervisor.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gray-200 border-2 border-white shadow-sm flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">{supervisor.name}</h4>
                <p className="text-sm text-gray-600">
                  {supervisor.credentials?.map(cred => 
                    CREDENTIAL_FULL_NAMES[cred] || cred
                  ).join(', ') || CREDENTIAL_FULL_NAMES[supervisor.credential] || supervisor.credential}
                </p>
                <p className="text-sm text-gray-600 mt-3">
                  {fill(
                    labels?.clinicalSupervisionBody ??
                      "Practicing under the supervision of a licensed therapist means that {name}'s work is closely monitored and guided by {supervisorFirstName}, an experienced and licensed professional. This ensures {name} receives the high quality training and guidance needed to support you through your therapeutic journey.",
                    {
                      name: firstName,
                      supervisor: supervisor.name,
                      supervisorFirstName: supervisor.name.split(' ')[0] ?? supervisor.name,
                    },
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
        
{/* Residency Program - Only for residents */}
        {therapistTier === "resident" && residencyCompletion && (
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 text-primary-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
              {labels?.residencyHeading ?? 'Residency Program'}
            </h3>

            <div className="space-y-3">
              {/* Timeline Card */}
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">{labels?.residencyCompletesOn ?? 'Completes on'}</p>
                      <p className="text-base font-semibold text-gray-900">
                        {new Date(residencyCompletion).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' , timeZone: 'America/Toronto'})}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                      {(() => {
                        const today = new Date();
                        const endDate = new Date(residencyCompletion);
                        const diffTime = endDate.getTime() - today.getTime();
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        const diffMonths = Math.floor(diffDays / 30);

                        if (diffDays < 0) return labels?.residencyCompleted ?? '✓ Completed';
                        if (diffDays === 0) return labels?.residencyToday ?? 'Today';
                        if (diffDays === 1) return labels?.residencyOneDayLeft ?? '1 day left';
                        if (diffDays < 30)
                          return fill(labels?.residencyDays ?? '{count} days', { count: diffDays });
                        if (diffMonths === 1) return labels?.residencyOneMonth ?? '1 month';
                        if (diffMonths < 12)
                          return fill(labels?.residencyMonths ?? '{count} months', { count: diffMonths });
                        const years = Math.floor(diffMonths / 12);
                        if (years === 1) return labels?.residencyOneYear ?? '1 year';
                        return fill(labels?.residencyYears ?? '{count} years', { count: years });
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Benefit Card */}
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900 mb-1">{labels?.transitionBenefitTitle ?? 'Your Transition Benefit'}</p>
                    <p className="text-sm text-gray-600">
                      {fill(
                        labels?.transitionBenefitBody ??
                          'Receive {count} complimentary sessions to seamlessly continue your therapy journey with another resident therapist',
                        { count: 3 },
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
      </div>
    </div>
  );
}